import { useEffect, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';

const TranslationWrapper = ({ children }) => {
  const { language } = useLanguage();
  const previousLanguageRef = useRef(language);

  useEffect(() => {
    if (previousLanguageRef.current && previousLanguageRef.current !== language) {
      window.location.reload();
    }
    previousLanguageRef.current = language;
  }, [language]);

  useEffect(() => {
    if (!language || !translations[language]) return;

    const preserveCase = (original, replacement) => {
      if (original === original.toUpperCase()) {
        return replacement.toUpperCase();
      }
      
      if (original === original.toLowerCase()) {
        return replacement.toLowerCase();
      }
      
      if (original[0] === original[0].toUpperCase()) {
        return replacement.charAt(0).toUpperCase() + replacement.slice(1).toLowerCase();
      }
      
      return replacement;
    };

    const translatePage = () => {
      const walker = document.createTreeWalker(
        document.body,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            if (!node.nodeValue || !node.nodeValue.trim()) {
              return NodeFilter.FILTER_REJECT;
            }
            
            const parent = node.parentElement;
            if (!parent) return NodeFilter.FILTER_REJECT;
            
            const tagName = parent.tagName?.toLowerCase();
            if (tagName === 'script' || tagName === 'style' || tagName === 'noscript') {
              return NodeFilter.FILTER_REJECT;
            }
            
            if (parent.contentEditable === 'true' || parent.isContentEditable) {
              return NodeFilter.FILTER_REJECT;
            }
            
            return NodeFilter.FILTER_ACCEPT;
          }
        }
      );

      const textNodes = [];
      let node;
      while (node = walker.nextNode()) {
        textNodes.push(node);
      }

      const dict = translations[language].common;
      
      textNodes.forEach(node => {
        let text = node.nodeValue;
        let modified = false;
        
        Object.entries(dict).forEach(([usWord, britishWord]) => {
          if (language === 'en-GB' && usWord !== britishWord) {
            const regex = new RegExp(`\\b${usWord}\\b`, 'gi');
            const newText = text.replace(regex, (match) => {
              return preserveCase(match, britishWord);
            });
            
            if (newText !== text) {
              text = newText;
              modified = true;
            }
          } else if (language === 'en-US' && usWord !== britishWord) {
            const regex = new RegExp(`\\b${britishWord}\\b`, 'gi');
            const newText = text.replace(regex, (match) => {
              return preserveCase(match, usWord);
            });
            
            if (newText !== text) {
              text = newText;
              modified = true;
            }
          }
        });
        
        if (modified) {
          node.nodeValue = text;
        }
      });
    };

    const timeoutId = setTimeout(translatePage, 100);

    const observer = new MutationObserver(() => {
      clearTimeout(timeoutId);
      setTimeout(translatePage, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });

    return () => {
      clearTimeout(timeoutId);
      observer.disconnect();
    };
  }, [language]);

  return children;
};

export default TranslationWrapper;